import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    type SupportRequestFormProps,
    SupportRequestModalForm,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/support-request-modal-form';
import {
    Banner,
    Callout,
    FileWithUrl,
    FlexContainer,
    Link,
    UtilityInfoIcon,
} from '@placer-ui/components';
import { ControlledTextArea } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { ControlledFileUpload } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-file-upload';
import { EditPoiDisplay } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/suggest-edit/edit-poi-display';
import { SupportEmailInput } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import { ControlledBasicSingleSelect } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-basic-single-select';
import { goToInANewTab } from 'router/go-to';
import { getFeatureFlags } from 'core/flow-control';
import type { PlaceType } from '@placer-ui/types';
import { useSubmitRequestFlowMutation } from 'features/offline-reports/support-requests/components/support-requests-modals/queries/submit-request-flow-mutation';
import { SupportRequestsData } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';

import styles from './suggest-edit.module.scss';

export interface SuggestEditFormValues {
    type: 'suggest_edit';
    poiName: string;
    address: string;
    id: string;
    entityType: PlaceType;
    files?: FileWithUrl[];
    subject?: string;
    suggestedEdit?: string;
    executiveEmail?: string;
    opportunityUrl?: string;
}

const fileSupportedFormats = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

const subjectItems = (enable_sqft_suggest_an_edit: boolean) => [
    {
        label: 'Update the property’s name',
        value: 'Update the property’s name',
    },
    {
        label: 'Update the property’s address',
        value: 'Update the property’s address',
    },
    {
        label: 'Update the opening or closing date',
        value: 'Update the opening or closing date',
    },
    {
        label: 'Update the opening hours',
        value: 'Update the opening hours',
    },
    ...(enable_sqft_suggest_an_edit
        ? [
              {
                  label: 'Update Square Footage / Gross Leasable Area',
                  value: 'Update Square Footage / Gross Leasable Area',
              },
          ]
        : []),
    {
        label: 'Adjust the property’s boundaries',
        value: 'Adjust the property’s boundaries',
    },
];

export const convertSuggestEditFormToRequestPayload = (values: SuggestEditFormValues) => ({
    ...values,
    files: values.files?.map(({ file }) => file),
});

export const SuggestEdit = (props: SupportRequestsData['suggestEdit']) => {
    const [bannerVisible, setBannerVisible] = useState(true);
    const hideBanner = () => setBannerVisible(false);
    const { poiName, address, id, entityType } = props || {};
    const {
        enable_files_suggest_an_edit,
        enable_sqft_suggest_an_edit,
        enable_request_properties_sales_flow_ff,
    } = getFeatureFlags();
    const { mutate: submitRequestFlowMutation } = useSubmitRequestFlowMutation();
    const requestEditConfig = {
        testId: 'support-requests-edit',
        title: 'Suggest an Edit',
        requestType: 'suggestEdit',
        subTitle: 'Let us know which details need to be corrected or updated',
        convertToRequestPayload: convertSuggestEditFormToRequestPayload,
    } as SupportRequestFormProps<SuggestEditFormValues>;

    const formController = useForm<SuggestEditFormValues>({
        defaultValues: {
            type: 'suggest_edit',
            poiName,
            address,
            id,
            entityType,
            subject: '',
            suggestedEdit: '',
            files: [],
        },
        mode: 'all',
    });

    const postSubmitAction = (case_id: string) => {
        submitRequestFlowMutation({
            case_id,
            endpoint: 'suggest-an-edit',
        });
    };

    return (
        <SupportRequestModalForm<SuggestEditFormValues>
            {...requestEditConfig}
            form={formController}
            withCloseButton
            withBackButton={false}
            postSubmitAction={postSubmitAction}
        >
            <FlexContainer
                flexDirection="column"
                gap={24}
                margin={enable_files_suggest_an_edit ? '0 0 56px 0' : '0 0 0 0'}
            >
                {bannerVisible && (
                    <Banner
                        layout="in-line"
                        testId="suggest-edit-info"
                        type="primary"
                        LeftIcon={<UtilityInfoIcon />}
                        onClose={hideBanner}
                        text="Placer uses proprietary algorithms to determine location details. While you’re welcome to submit additional information for our team's review, these contributions may not directly impact our methodology."
                    />
                )}
                <EditPoiDisplay poi={{ poiName, address }} />
                {enable_request_properties_sales_flow_ff && (
                    <FlexContainer gap={16}>
                        <SupportSalesEmailInput />
                        <SupportOpportunityUrlInput />
                    </FlexContainer>
                )}
                {!enable_request_properties_sales_flow_ff && <SupportEmailInput />}
                <div className={styles.inputWrapper}>
                    <ControlledBasicSingleSelect
                        name="subject"
                        testId="suggest-edit-subject"
                        label="Subject"
                        placeholder="Select Option"
                        control={formController.control}
                        items={subjectItems(!!enable_sqft_suggest_an_edit)}
                        required
                        asterix
                    />
                </div>
                <ControlledTextArea
                    name="suggestedEdit"
                    testId="suggest-edit-suggested-edit"
                    placeholder="Briefly describe the edit needed"
                    label="Suggested Edit"
                    control={formController.control}
                    rules={{
                        required: { value: true, message: 'This field is required' },
                    }}
                    maxLength={200}
                    initialHeight={56}
                    disabled={!formController.watch('subject')}
                    required
                    asterix
                />
                {enable_files_suggest_an_edit && (
                    <ControlledFileUpload
                        name="files"
                        label="Attachments"
                        testId="suggest-edit-files-input"
                        formats={fileSupportedFormats}
                        feedback={[]}
                        description="JPEG, PNG, PDF"
                    />
                )}
                <div className={styles.callout}>
                    <Callout
                        FreeTextIcon={<UtilityInfoIcon />}
                        text="Couldn't find what you're looking for? Visit our"
                        CTA={
                            <Link
                                onClick={() =>
                                    goToInANewTab('my-zone/my-requests/support-requests')
                                }
                                testId="suggest-edit-support-center-link"
                            >
                                Support Request Center
                            </Link>
                        }
                    />
                </div>
            </FlexContainer>
        </SupportRequestModalForm>
    );
};
